import type {VisualReport} from "./visual-report";

type JsonRecord = Record<string, unknown>;

function toStringRecord(values: unknown, keys: string[]): Record<string, string> {
    if (!Array.isArray(values)) return {};
    return Object.fromEntries(
        keys.map((key, idx) => [key, String(values[idx] ?? "")]),
    );
}

function normalizeBlock(block: unknown): JsonRecord | null {
    if (!block || typeof block !== "object") return null;
    const b: JsonRecord = {...(block as JsonRecord)};

    // Agent often sends block_type instead of type
    if (!b.type && typeof b.block_type === "string") {
        b.type = b.block_type;
        delete b.block_type;
    }

    if (b.type === "table") {
        b.type = "comparison_table";
    }

    if (b.type === "metric_grid" && !Array.isArray(b.items) && Array.isArray(b.metrics)) {
        b.items = b.metrics;
    }

    if (b.type === "comparison_table") {
        const headers = Array.isArray(b.headers)
            ? b.headers.map(h => String(h))
            : [];
        if (!Array.isArray(b.columns) && headers.length > 0) {
            b.columns = headers.map(h => ({key: h, label: h, align: "left"}));
        }
        if (Array.isArray(b.rows) && b.rows.length > 0 && Array.isArray((b.rows as unknown[])[0])) {
            const keys = Array.isArray(b.columns)
                ? (b.columns as JsonRecord[]).map(col => String(col.key ?? ""))
                : headers;
            b.rows = (b.rows as unknown[]).map(row => ({
                values: toStringRecord(row, keys),
            }));
        }
    }

    // Bridge table: normalize string[] columns → column objects, cells[] rows → values records
    if (b.type === "bridge_table") {
        if (Array.isArray(b.columns) && b.columns.length > 0 && typeof b.columns[0] === "string") {
            const colStrings = b.columns as string[];
            b.columns = colStrings.map(h => ({key: h, label: h, align: h === colStrings[0] ? "left" : "right"}));
        }
        const cols = Array.isArray(b.columns) ? (b.columns as JsonRecord[]) : [];
        const colKeys = cols.map(c => String(c.key ?? ""));
        if (Array.isArray(b.rows)) {
            b.rows = (b.rows as JsonRecord[]).map(row => {
                if (Array.isArray(row.cells)) {
                    return {
                        ...row,
                        values: Object.fromEntries(colKeys.map((k, i) => [k, String((row.cells as unknown[])[i] ?? "")])),
                    };
                }
                if (!row.values && !row.cells) {
                    return row;
                }
                return row;
            });
        }
    }

    // Data table: normalize headers + array-of-arrays → columns + row objects
    if (b.type === "data_table") {
        const headers = Array.isArray(b.headers) ? b.headers.map(h => String(h)) : [];
        if (!Array.isArray(b.columns) && headers.length > 0) {
            b.columns = headers.map((h, i) => ({key: h, label: h, align: i === 0 ? "left" : "right"}));
        }
        const cols = Array.isArray(b.columns) ? (b.columns as JsonRecord[]) : [];
        const colKeys = cols.map(c => String(c.key ?? ""));
        if (Array.isArray(b.rows) && b.rows.length > 0 && Array.isArray(b.rows[0])) {
            b.rows = (b.rows as unknown[]).map(row => ({
                values: toStringRecord(row, colKeys),
            }));
        }
    }

    // Chart with echarts_builder: normalize data/xField/yField → categories/series
    if (b.type === "chart" && b.chart_engine === "echarts_builder" && b.builder_config) {
        const cfg = b.builder_config as JsonRecord;
        if (Array.isArray(cfg.data) && typeof cfg.xField === "string" && !cfg.categories) {
            const dataArr = cfg.data as JsonRecord[];
            const xField = cfg.xField as string;
            const yField = (cfg.yField as string) ?? "value";
            cfg.categories = dataArr.map(d => String(d[xField] ?? ""));
            cfg.series = [{
                key: yField,
                label: (cfg.title as string) ?? yField,
                data: dataArr.map(d => Number(d[yField] ?? 0)),
            }];
            delete cfg.data;
            delete cfg.xField;
            delete cfg.yField;
            delete cfg.title;
        }
    }

    if (b.type === "narrative" && typeof b.body !== "string") {
        b.body = String(b.body ?? "");
    }

    return b;
}

export function normalizeArtifactReport(report: unknown): VisualReport | null {
    if (!report || typeof report !== "object") return null;
    const raw = report as JsonRecord;

    if (!Array.isArray(raw.blocks)) {
        return null;
    }

    const normalizedBlocks = raw.blocks
        .map(normalizeBlock)
        .filter((b): b is JsonRecord => b !== null && typeof b.type === "string");

    if (normalizedBlocks.length === 0 && raw.blocks.length > 0) {
        return null;
    }

    return {
        report_type: typeof raw.report_type === "string" && raw.report_type
            ? raw.report_type
            : "sandbox_artifact",
        title: typeof raw.title === "string" && raw.title
            ? raw.title
            : "Sandbox Artifact",
        subtitle: typeof raw.subtitle === "string" ? raw.subtitle : undefined,
        blocks: normalizedBlocks as unknown as VisualReport["blocks"],
    };
}
